import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { explain } from '@/shared/api/problem'
import { ErrorStrip, Forbidden, LoadingBlock } from '@/shared/ui/states'
import { useCan } from '@/shared/auth/session'
import { getEstimator, listPolls, type Estimator, type EstimatorSeparation } from './api'
import type { SurveyResponseDto } from '@/shared/api/types'
import {
  Chain, DesignCost, Panel, Provenance, Reading, Stat, ThinCells, Weighting,
} from './estimator/apparatus'
import {
  GAP_MARGIN, drawField, drawGap, hitLane, layoutLanes, readTheme,
  type FieldScene, type Lane, type Theme,
} from './estimator/field'
import { count, pct, pp, probability, shareDomain } from './estimator/scale'
import { Scoreboard } from './estimator/Scoreboard'
import { NextMove } from './estimator/NextMove'

/**
 * The Estimator — the Polling Studio's landing board.
 *
 * ## The question, and why it needed its own screen
 *
 * Results already answers "what is the number", and answers it carefully: every share on it
 * arrives with its base, its design effect and its margin attached. This answers the question
 * underneath that one — <b>how much weight will the number take?</b> — which is what a campaign
 * is actually asking when it reads a two-point lead and decides where to spend a week.
 *
 * ## Three things a point estimate hides, and one figure for each
 *
 * **An estimate is a distribution.** Figure 1 draws every option as the sampling distribution
 * it is, with equal area under each curve, in two overprinting inks. Where two candidates cover
 * the same share the paper takes both and darkens, and that is the region the poll cannot
 * resolve — produced by the ink, not by a label.
 *
 * **A lead has its own margin, and it is not the poll's.** Figure 2 draws the difference
 * between the top two as one distribution with zero marked. The two shares come from a single
 * multinomial and are negatively correlated, so the gap is better measured than either share —
 * which means two candidates whose own intervals overlap are routinely, correctly, separated.
 * The board says so in the case where it happens, because the overlap test is the one every
 * chart with error bars invites and it is the wrong one.
 *
 * **The interval covers sampling error and nothing else.** Figures 3 and 4 put what the design
 * cost in precision, and the half of the eligible frame that never became an interview, beside
 * it — because a ±3 on a poll with a 47% response rate is a statement about arithmetic rather
 * than about the electorate.
 *
 * ## Figures left, apparatus right
 *
 * Deliberately not the three-column stage the other two consoles use. There is nothing here to
 * filter — one question, six options, all six already drawn — so a rail of controls would be
 * furniture. What belongs beside a figure is its apparatus, which is what a paper puts there.
 *
 * ## Nothing on this screen computes a percentage
 *
 * The module's standing rule holds. Every share, margin, design effect, rate and probability is
 * the server's; the curves are a rendering of a served `(estimate, standardError)` pair and the
 * shaded area under Figure 2 is the served probability, not a second calculation of it.
 */
export function EstimatorPage() {
  const canAnalyse = useCan('survey:analyse')
  const [params, setParams] = useSearchParams()
  const pollId = params.get('poll')
  const [polls, setPolls] = useState<SurveyResponseDto[] | null>(null)
  const [data, setData] = useState<Estimator | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [questionId, setQuestionId] = useState<string | null>(null)
  const [hovered, setHovered] = useState<string | null>(null)
  const [selected, setSelected] = useState<string | null>(null)
  /**
   * Whether the scientific plates are on screen.
   *
   * Off by default, and that is the change: the figures are correct and were the reason an
   * aspirant could not read this board. They are a click away, not gone.
   */
  const [showPlates, setShowPlates] = useState(false)
  const [showIntervals, setShowIntervals] = useState(true)

  const rootRef = useRef<HTMLDivElement>(null)
  const fieldWrapRef = useRef<HTMLDivElement>(null)
  const fieldRef = useRef<HTMLCanvasElement>(null)
  const gapRef = useRef<HTMLCanvasElement>(null)
  const themeRef = useRef<Theme | null>(null)
  const lanesRef = useRef<Lane[]>([])
  const sizeRef = useRef({ w: 0, h: 0, dpr: 1 })

  // ── data ───────────────────────────────────────────────────────────────────

  useEffect(() => {
    let live = true
    listPolls({}, 0, 100)
      .then((page) => { if (live) setPolls(page.content) })
      .catch((err) => { if (live) setError(explain(err)) })
    return () => { live = false }
  }, [])

  /**
   * Land on a poll rather than on a picker.
   *
   * A console whose first state is "choose something" has spent the reader's first glance on
   * an errand. The preference is ordered by what this screen can actually say something about:
   * a poll that has stopped collecting has figures, a live one usually does not, and a draft
   * never will. Falling through to the first poll rather than to nothing matters too — the
   * board's own "no weight run has completed" sentence is a better first screen than a picker,
   * because it names the next step.
   */
  useEffect(() => {
    if (pollId || !polls || polls.length === 0) return
    const analysable = new Set(['CLOSED', 'ARCHIVED'])
    const best = polls.find((p) => analysable.has(p.statusCode))
      ?? polls.find((p) => p.collectedCount > 0)
      ?? polls[0]
    if (!best) return
    const next = new URLSearchParams(params)
    next.set('poll', best.id)
    setParams(next, { replace: true })
  }, [pollId, polls, params, setParams])

  useEffect(() => {
    if (!pollId) return
    let live = true
    setError(null)
    getEstimator(pollId, questionId ?? undefined)
      .then((res) => { if (live) setData(res) })
      .catch((err) => { if (live) setError(explain(err)) })
    return () => { live = false }
  }, [pollId, questionId])

  // A different poll is a different instrument: keeping the question id would ask the server
  // for a question that does not exist on it, and the fallback would silently answer with
  // another one.
  useEffect(() => { setQuestionId(null); setSelected(null) }, [pollId])

  // ── the figures ────────────────────────────────────────────────────────────

  const domain = useMemo(() => shareDomain(data?.bands ?? []), [data])
  const lead = data?.separations[0] ?? null

  /**
   * The share range the top two intervals share, from the SERVED bounds.
   *
   * Read off `ciLower`/`ciUpper` rather than recomputed from the estimate and the margin: the
   * server already decided where those bounds sit, and a second derivation here would be a
   * second answer that could round differently from the one printed in the rail.
   */
  const intervalOverlap = useMemo(() => {
    if (!lead || !data) return null
    const a = data.bands.find((b) => b.optionId === lead.aOptionId)
    const b = data.bands.find((x) => x.optionId === lead.bOptionId)
    if (!a || !b || a.ciLower === null || b.ciUpper === null) return null
    return a.ciLower < b.ciUpper ? { from: a.ciLower, to: b.ciUpper } : null
  }, [data, lead])

  const render = useCallback(() => {
    const theme = themeRef.current
    const field = fieldRef.current
    if (!theme || !field || !data) return
    const ctx = field.getContext('2d')
    if (!ctx) return

    const scene: FieldScene = {
      bands: data.bands,
      domain,
      width: sizeRef.current.w,
      height: sizeRef.current.h,
      dpr: sizeRef.current.dpr,
      theme,
      hovered,
      selected,
      showIntervals,
      overlap: intervalOverlap,
    }
    const lanes = layoutLanes(scene)
    lanesRef.current = lanes
    drawField(ctx, scene, lanes)

    const gap = gapRef.current
    const gctx = gap?.getContext('2d')
    if (gap && gctx && lead) {
      drawGap(gctx, {
        separation: lead,
        width: sizeRef.current.w,
        height: gap.clientHeight,
        dpr: sizeRef.current.dpr,
        theme,
      })
    }
    // showPlates is a dependency because the canvases UNMOUNT when the plates are hidden:
    // without it this memo keeps a closure over a null ref, returns early for ever, and
    // revealing the statistics again shows two blank canvases that are never sized or drawn.
  }, [data, domain, hovered, selected, showIntervals, lead, intervalOverlap, showPlates])

  useEffect(() => {
    const root = rootRef.current
    const wrap = fieldWrapRef.current
    const field = fieldRef.current
    const gap = gapRef.current
    if (!root || !wrap || !field) return

    themeRef.current = readTheme(root)

    const resize = () => {
      const rect = wrap.getBoundingClientRect()
      const dpr = Math.min(window.devicePixelRatio || 1, 2)
      const w = Math.max(60, rect.width)
      const h = Math.max(60, rect.height)
      sizeRef.current = { w, h, dpr }
      field.width = w * dpr
      field.height = h * dpr
      field.style.width = `${w}px`
      field.style.height = `${h}px`
      if (gap) {
        const gh = gap.clientHeight || 120
        gap.width = w * dpr
        gap.height = gh * dpr
        gap.style.width = `${w}px`
      }
      render()
    }
    resize()
    const observer = new ResizeObserver(resize)
    observer.observe(wrap)

    // The inks live in CSS custom properties, so a theme change has to be re-read: the
    // rasterised numbers were resolved against the old ones, and the overprint would be
    // computed in yesterday's colours.
    const onTheme = () => { themeRef.current = readTheme(root); render() }
    const media = window.matchMedia('(prefers-color-scheme: dark)')
    media.addEventListener('change', onTheme)
    const watch = new MutationObserver(onTheme)
    watch.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] })

    return () => {
      observer.disconnect()
      media.removeEventListener('change', onTheme)
      watch.disconnect()
    }
    // Same reason as the render memo above: the observer has nothing to observe while the
    // plates are hidden, so it has to be rebuilt when they come back.
  }, [render, showPlates])

  useEffect(() => { render() }, [render])

  const onMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const rect = e.currentTarget.getBoundingClientRect()
    const hit = hitLane(lanesRef.current, e.clientY - rect.top)
    if (hit !== hovered) setHovered(hit)
  }

  const setPoll = (id: string) => {
    const next = new URLSearchParams(params)
    if (id) next.set('poll', id)
    else next.delete('poll')
    setParams(next, { replace: true })
  }

  // ── render ─────────────────────────────────────────────────────────────────

  if (!canAnalyse) return <Forbidden what="poll estimates" />
  if (error && !data) return <ErrorStrip message={error} />
  if (!polls || (pollId && !data)) return <LoadingBlock label="Reading the estimate" />

  const selectedBand = data?.bands.find((b) => b.optionId === selected) ?? null

  return (
    <div ref={rootRef}
         className="axis-riso relative -mx-3 -mb-3 flex h-[calc(100dvh-9.5rem)] min-h-[600px]
                    flex-col overflow-hidden border"
         style={{ borderColor: 'var(--est-rule)' }}>

      {/* ── masthead ── */}
      <header className="flex shrink-0 flex-wrap items-stretch border-b"
              style={{ borderColor: 'var(--est-rule)' }}>
        <div className="flex items-center gap-3 border-r px-4 py-2.5"
             style={{ borderColor: 'var(--est-rule)' }}>
          <Mark />
          <span className="flex flex-col justify-center">
            <b className="text-[17px] leading-tight font-light"
               style={{ color: 'var(--est-ink)' }}>The Estimator</b>
            <span className="axis-riso-eyebrow mt-px">Where the poll stands, and how
              certain it is</span>
          </span>
        </div>

        <div className="flex flex-1 flex-wrap items-center gap-3 px-4">
          <label className="flex items-center gap-1.5">
            <span className="axis-riso-eyebrow">Poll</span>
            <select value={pollId ?? ''} onChange={(e) => setPoll(e.target.value)}
                    className="axis-riso-select min-w-[240px]">
              <option value="">Choose a poll…</option>
              {polls.map((p) => (
                <option key={p.id} value={p.id}>{p.ref} · {p.title}</option>
              ))}
            </select>
          </label>
          {data && data.questions.length > 0 ? (
            <label className="flex items-center gap-1.5">
              <span className="axis-riso-eyebrow">Question</span>
              <select value={questionId ?? data.question?.id ?? ''}
                      onChange={(e) => setQuestionId(e.target.value)}
                      className="axis-riso-select min-w-[220px] max-w-[380px]">
                {data.questions.map((q) => (
                  <option key={q.id} value={q.id}>{q.code} · {q.text}</option>
                ))}
              </select>
            </label>
          ) : null}
        </div>

        {data?.design ? (
          <div className="flex items-center gap-6 border-l px-5"
               style={{ borderColor: 'var(--est-rule)' }}>
            {/* The labels are the plain-language ones. "deff" and "effective n" are the
                correct terms and they are what a methodologist wants; they are also what
                makes this board unreadable to the person who commissioned the poll. The
                term of art is kept in the note underneath, so nothing is lost — but the
                label answers the question the reader actually has. */}
            <Stat label="Interviews" value={count(data.design.baseN)}
                  note="People actually spoken to — the base every figure rests on" />
            <Stat label="As accurate as" value={count(data.design.effectiveN)} tone="b"
                  note="Effective n: the ideal random sample that would be this precise" />
            <Stat label="Precision lost" value={
                    data.design.deff != null
                      ? `${Math.round((1 - 1 / data.design.deff) * 100)}%` : '—'}
                  note={`Design effect ${data.design.deff?.toFixed(2) ?? '—'}, measured from the realised weights, never assumed`} />
            {lead ? (
              <Stat label="Lead is real" value={probability(lead.leadProbability)}
                    tone={lead.separated ? 'good' : 'b'}
                    note="Chance the leader is genuinely ahead, from the margin on the gap" />
            ) : null}
          </div>
        ) : null}

        <div className="flex items-center border-l px-2" style={{ borderColor: 'var(--est-rule)' }}>
          <button type="button" aria-pressed={showIntervals}
                  onClick={() => setShowIntervals((v) => !v)} className="axis-riso-toggle">
            Intervals
          </button>
        </div>
      </header>

      {data?.unavailable ? (
        <div className="flex flex-1 items-center justify-center px-10">
          <p className="max-w-lg text-center text-[13px] leading-relaxed"
             style={{ color: 'var(--est-ink-dim)' }}>{data.unavailable}</p>
        </div>
      ) : null}

      {data && !data.unavailable ? (
        <div className="grid min-h-0 flex-1"
             style={{ gridTemplateColumns: '1fr 372px', gap: 1,
                      background: 'var(--est-rule)' }}>

          {/* ── the figures ── */}
          <div className="grid min-h-0 min-w-0"
               style={{ gridTemplateRows: 'auto 1fr auto', gap: 1, background: 'var(--est-rule)' }}>

            {/*
               THE PLAIN BOARD IS THE BOARD NOW. The plates are one switch away.

               What used to occupy this space was two scientific figures — every option as a
               sampling distribution with equal area under each curve, then the gap as its own
               distribution with the probability shaded under the tail. Both are the correct
               instrument for their question. Both were also unreadable to the person who
               commissioned the poll, and that person is the one deciding where a week of
               money goes. A board only an analyst can read gets ignored, and the decision
               then gets made on instinct, which is what the poll was bought to replace.

               So the landing view answers the three questions an aspirant actually arrives
               with — where do I stand, can I trust it, what do I do — and the apparatus is
               still there, intact, for whoever wants it.
            */}
            <PlainReading data={data} lead={lead} />

            <section style={{ background: 'var(--est-paper)' }}>
              <div className="flex items-baseline justify-between gap-3 px-4 pt-3">
                <span className="axis-riso-eyebrow">Where everyone stands</span>
                <span className="axis-riso-mini">
                  {data.question?.text ?? 'This question'}
                </span>
              </div>
              <Scoreboard data={data} />
            </section>

            <section style={{ background: 'var(--est-paper)' }}>
              <div className="px-4 pt-3">
                <span className="axis-riso-eyebrow">What to do about it</span>
              </div>
              <NextMove data={data} />
            </section>

            <section style={{ background: 'var(--est-paper)' }}>
              <button type="button" onClick={() => setShowPlates((v) => !v)}
                      aria-expanded={showPlates}
                      className="w-full px-4 py-2 text-left axis-riso-eyebrow
                                 hover:underline">
                {showPlates ? '▾ Hide the statistics' : '▸ Show the statistics'}
                <span className="ml-2 axis-riso-mini">
                  the distributions, the design effect and the fieldwork record
                </span>
              </button>
            </section>

            {showPlates ? (
            <>
            <section className="flex min-h-0 min-w-0 flex-col"
                     style={{ background: 'var(--est-paper)' }}>
              <div className="flex shrink-0 items-baseline justify-between gap-3 px-4 pt-3">
                {/* Named for the question, not numbered like a plate. "Figure 1 · The
                    interval field" is exactly right and tells a candidate nothing; the
                    instrument is still named in the note beside it for anybody who wants
                    it. */}
                <span className="axis-riso-eyebrow">
                  How the race looks
                </span>
                <span className="axis-riso-mini">
                  Equal area under each curve — width is uncertainty, not support
                </span>
              </div>
              <p className="shrink-0 px-4 pt-1 text-[12px] leading-snug"
                 style={{ color: 'var(--est-ink-dim)' }}>
                {data.question?.text}
              </p>
              <div ref={fieldWrapRef} className="relative min-h-0 min-w-0 flex-1">
                <canvas ref={fieldRef} className="block size-full"
                        role="img"
                        aria-label={`Sampling distributions for ${data.bands.length} options. `
                          + data.bands
                            .filter((b) => b.estimate !== null)
                            .map((b) => `${b.optionLabel} ${pct(b.estimate)} ±${pp(b.moe95)}`)
                            .join('; ')}
                        onMouseMove={onMove}
                        onMouseLeave={() => setHovered(null)}
                        onClick={() => setSelected(hovered === selected ? null : hovered)} />
              </div>
            </section>

            <section className="shrink-0" style={{ background: 'var(--est-paper)' }}>
              <div className="flex items-baseline justify-between gap-3 px-4 pt-3">
                <span className="axis-riso-eyebrow">
                  How big the lead is — and whether it holds
                </span>
                <span className="axis-riso-mini">
                  {lead?.separated
                    ? 'Shaded area = the probability the lead is real'
                    : 'The mass either side of zero is the whole answer'}
                </span>
              </div>
              <canvas ref={gapRef} className="block h-[132px] w-full"
                      role="img"
                      aria-label={lead
                        ? `Distribution of the gap between ${lead.aLabel} and ${lead.bLabel}: `
                          + `${pp(lead.gap)} plus or minus ${pp(lead.gapMoe95)}. `
                          + `Probability the lead holds: ${probability(lead.leadProbability)}.`
                        : 'No gap to draw.'} />
              <p className="px-4 pb-3 axis-riso-mini leading-relaxed"
                 style={{ paddingLeft: GAP_MARGIN.left }}>
                The margin on a difference is not √2 times the poll's. Both shares come from one
                sample, so they move against each other — the server uses
                {' '}<code>[p₁ + p₂ − (p₁ − p₂)²] / n_eff</code>.
              </p>
            </section>
            </>
            ) : null}
          </div>

          {/* ── the apparatus ── */}
          <aside className="axis-riso-column min-h-0 overflow-y-auto">
            <Panel figure="Reading" title="The numbers behind that sentence"
                   storageKey="reading">
              {selectedBand ? (
                <div className="mb-3 border-l pl-3"
                     style={{ borderColor: 'var(--est-a)' }}>
                  <b className="block text-[13px] font-medium"
                     style={{ color: 'var(--est-ink)' }}>{selectedBand.optionLabel}</b>
                  <span className="axis-riso-mini">
                    {selectedBand.suppressed
                      ? selectedBand.suppressedSentence
                      : `${pct(selectedBand.estimate)} ± ${pp(selectedBand.moe95)} · `
                        + `95% interval ${pct(selectedBand.ciLower)} to `
                        + pct(selectedBand.ciUpper)}
                  </span>
                </div>
              ) : null}
              <Reading separation={lead} budget={data.budget} />
            </Panel>

            <Panel title="What balancing the sample cost" startClosed
                   storageKey="cost" maxBodyHeight={260}>
              <DesignCost budget={data.budget} design={data.design} />
            </Panel>

            <Panel title="Who we could not reach" startClosed
                   note={data.attrition ? `n ${count(data.attrition.complete)}` : undefined}
                   storageKey="chain" maxBodyHeight={352}>
              <Chain attrition={data.attrition} />
            </Panel>

            <Panel title="Where this poll came from" storageKey="provenance"
                   maxBodyHeight={200} startClosed>
              <Provenance design={data.design} poll={data.poll} />
            </Panel>

            <Panel title="How the sample was balanced" note={`${data.weighting.length}`}
                   startClosed
                   storageKey="weighting" maxBodyHeight={220}>
              <Weighting cells={data.weighting} />
            </Panel>

            <Panel title="Groups too small to report" startClosed
                   note={`${data.subgroups.filter((s) => s.thin).length} thin`}
                   storageKey="subgroups" maxBodyHeight={240}>
              <ThinCells subgroups={data.subgroups} />
            </Panel>
          </aside>
        </div>
      ) : null}

      {/* ── colophon ── */}
      <footer className="flex shrink-0 flex-wrap items-center gap-x-5 gap-y-1 border-t px-4 py-2"
              style={{ borderColor: 'var(--est-rule)' }}>
        {data ? (
          <>
            <span className="axis-riso-mini">
              {data.poll.ref} · {data.poll.title} · {data.poll.statusCode}
            </span>
            <span className="axis-riso-mini">
              Computed {data.design?.computedAt
                ? new Date(data.design.computedAt).toLocaleString()
                : '—'}
            </span>
            <span className="axis-riso-mini">
              95% intervals · {data.design?.methodCode ?? 'unweighted'}
            </span>
            <span className="axis-riso-mini">
              The margin covers who we happened to ask. It does not cover the people we
              never reached — those are counted under &ldquo;Who we could not reach&rdquo;
            </span>
          </>
        ) : null}
        {error ? (
          <span className="axis-riso-mini" style={{ color: 'var(--est-b)' }}>{error}</span>
        ) : null}
      </footer>
    </div>
  )
}

/**
 * The console's mark.
 *
 * A normal curve over a baseline with its interval ticked off — the one figure this whole
 * screen is an argument about, at 28 pixels. Drawn rather than imported so it takes the ink
 * token and needs no asset.
 */
/**
 * The poll, in the words somebody would use out loud.
 *
 * ## Why this sits above the figures rather than beside them
 *
 * Everything below it is right, and none of it is legible to the person who paid for the
 * poll. "deff 1.38", "n_eff 872", "the intervals overlap but the separation is significant
 * at 98%" are four true statements that together answer no question a candidate has. The
 * three they DO have are: am I ahead, can I trust it, and what would it cost to be sure —
 * so those are the three sentences here, in that order, built from the same served figures
 * as the curves underneath.
 *
 * This is a translation, not a simplification. Nothing here rounds away a caveat: when the
 * lead is inside the margin the sentence says the poll cannot tell, and when the overlap
 * test and the gap test disagree it says which one is the right one and why. The apparatus
 * stays on the page precisely so the claim can be checked — a plain sentence with nothing
 * behind it is how polls get misreported in the first place.
 */
function PlainReading({ data, lead }: { data: Estimator; lead: EstimatorSeparation | null }) {
  const pctOf = (v: number | null | undefined, d = 0) =>
    v == null ? '—' : `${(v * 100).toFixed(d)}%`
  const pts = (v: number | null | undefined, d = 1) =>
    v == null ? '—' : `${(v * 100).toFixed(d)} points`

  const top = data.bands.filter((b) => !b.suppressed && b.estimate != null)
    .sort((a, b) => (b.estimate ?? 0) - (a.estimate ?? 0))[0]
  const a = data.attrition
  const b = data.budget
  const thin = data.subgroups.filter((s) => s.thin).length

  const headline = !lead || !top
    ? 'This question has no two options to compare.'
    : lead.separated
      ? `${lead.aLabel} is ahead of ${lead.bLabel}, and the poll can say so.`
      : `${lead.aLabel} is ahead of ${lead.bLabel} on the raw numbers, but this poll cannot prove it.`

  return (
    <section className="px-4 py-3" style={{ background: 'var(--est-paper)' }}>
      <span className="axis-riso-eyebrow">In plain English</span>
      <p className="mt-1 text-[15px] leading-snug font-medium"
         style={{ color: 'var(--est-ink)' }}>{headline}</p>

      <div className="mt-2.5 grid gap-x-6 gap-y-2 md:grid-cols-3">
        <PlainPoint label="Am I ahead?">
          {lead && top ? (
            <>
              {top.optionLabel} is on <b>{pctOf(top.estimate, 1)}</b>, {lead.bLabel} on{' '}
              <b>{pctOf(data.bands.find((x) => x.optionId === lead.bOptionId)?.estimate, 1)}</b> —
              a gap of <b>{pts(lead.gap)}</b>, give or take {pts(lead.gapMoe95)}.{' '}
              {lead.separated
                ? `The gap is bigger than its own error, so the lead survives. There is roughly a ${pctOf(lead.leadProbability)} chance it is genuine.`
                : 'The gap is smaller than its own error, so run the same poll again and the order could flip. Say "ahead, but within the margin" — not "leads".'}
              {lead.separated && lead.intervalsOverlap
                ? ' Ignore anybody who says the bars overlap so it is a tie: that test is the wrong one for two shares of the same question, and it is too cautious.'
                : ''}
            </>
          ) : 'Not enough published options on this question to compare two of them.'}
        </PlainPoint>

        <PlainPoint label="Can I trust it?">
          {/* The fieldwork log can exist and still be empty — a poll whose figures were
              loaded rather than collected here. Reading `complete` straight off it then
              prints "built on 0 completed interviews" directly underneath a masthead
              saying 1,200, which reads as a broken page. Fall back to the base the
              figures were actually computed on, and say plainly what is missing. */}
          {a && a.complete > 0 ? (
            <>
              It is built on <b>{count(a.complete)}</b> completed interviews
              {a.responseRate1 != null
                ? <>, and <b>{pctOf(a.responseRate1)}</b> of the people approached took part</>
                : null}.{' '}
              {a.flagged > 0
                ? `${count(a.flagged)} interviews are flagged and left out until somebody checks them. `
                : ''}
              {b?.inflation != null && b.inflation > 1.2
                ? `Correcting for who answered has cost precision — the poll is as accurate as one with about ${count(b.effectiveN)} interviews, not ${count(b.baseN)}.`
                : 'The corrections applied to it are small, so the raw sample and the published figures say much the same thing.'}
            </>
          ) : b ? (
            <>
              It is built on <b>{count(b.baseN)}</b> interviews. Nobody logged who was
              approached and who refused, so how well those interviews stand in for
              everybody cannot be checked from here.{' '}
              {b.inflation != null && b.inflation > 1.2
                ? `Correcting for who answered has cost precision — the poll is as accurate as one with about ${count(b.effectiveN)} interviews, not ${count(b.baseN)}.`
                : 'The corrections applied to it are small, so the raw sample and the published figures say much the same thing.'}
            </>
          ) : 'No fieldwork record is attached to this poll, so its reliability cannot be judged from here.'}
        </PlainPoint>

        <PlainPoint label="What can it not tell me?">
          {thin > 0
            ? <>There are too few interviews to break the result down by{' '}
                <b>{data.subgroups.filter((s) => s.thin).slice(0, 3)
                  .map((s) => `${s.dimensionCode.toLowerCase()} ${s.valueCode.toLowerCase()}`)
                  .join(', ')}</b>
                {thin > 3 ? ` and ${thin - 3} more` : ''}. Those cuts need their own poll, not a
                smaller slice of this one.</>
            : 'Every subgroup in this poll has enough interviews to report, so the breakdowns below can be quoted as they stand.'}
          {b && b.lostInterviews > 0
            ? <> Buying back the precision the weighting cost would take roughly{' '}
                <b>{count(b.lostInterviews)}</b> more interviews.</>
            : null}
        </PlainPoint>
      </div>
    </section>
  )
}

function PlainPoint({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <span className="axis-riso-eyebrow">{label}</span>
      <p className="mt-0.5 text-[12.5px] leading-relaxed" style={{ color: 'var(--est-ink-dim)' }}>
        {children}
      </p>
    </div>
  )
}

function Mark() {
  return (
    <svg viewBox="0 0 32 32" className="size-7 shrink-0" aria-hidden focusable="false"
         fill="none" stroke="currentColor" style={{ color: 'var(--est-a)' }}>
      <path d="M2 24 C 8 24, 9 7, 16 7 C 23 7, 24 24, 30 24" strokeWidth="1.5" />
      <path d="M2 24 H30" strokeWidth="1" strokeOpacity=".45" />
      <path d="M9 21.5 H23" strokeWidth="1.2" strokeOpacity=".75" />
      <path d="M9 19.5 V23.5 M23 19.5 V23.5" strokeWidth="1.2" strokeOpacity=".75" />
      <path d="M16 7 V24" strokeWidth="1" strokeOpacity=".5" strokeDasharray="2 2" />
    </svg>
  )
}
